import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { Card } from '@/components/layout/Card/Card';
import { FOOTER_HEIGHT } from '@/components/navigation/Footer';
import { InPageNavigationBox } from '@/components/navigation/InPageNavigation/InPageNavigationBox';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { useGrowthProfileModalContext } from '../GrowthProfileModal.context';
import {
  GrowthProfileModalFormPaths,
  GrowthProfileModalFormShape,
} from '../GrowthProfileModal.types';
import { EntityTaxDragOverridesEditor } from './EntityTaxDragOverridesEditor';
import { mapTaxDragEntity } from './TaxDrag.entityUtils';
import { useTaxDragQuery } from './TaxDrag.generated';
import { TaxDragBaseMode } from './TaxDrag.types';
import { TaxDragRateField } from './TaxDragRateField';
import { TaxPayerSettings } from './TaxPayerSettings';

const TAX_DRAG_SECTION_ID = 'tax-drag-settings';
const TAX_PAYER_SECTION_ID = 'tax-payer-settings';
const TAX_DRAG_EDITOR_CONTENT_ID = 'tax-drag-editor-content';
const GLOBAL_TAX_DRAG_HELP_BODY =
  "The entity level tax drag entered will take priority over asset category defaults set at the firm level. Tax drag is the amount of annual return assumed to be used to pay taxes on investment income. This reduces the growth rate modeled in the Estate waterfall. For example, if a $1,000,000 portfolio has a 10% pre-tax return, and a 20% tax drag, then 20% of the $100,000 pre-tax investment return, or $20,000, is assumed to be the tax due on the portfolio's investment income.";

export function TaxDragEditor() {
  const { householdId } = useGrowthProfileModalContext();
  const { control } = useFormContext<GrowthProfileModalFormShape>();
  const { data } = useTaxDragQuery({ variables: { householdId } });
  const [taxDrag] = useWatch({ control, name: ['taxDrag'] });

  const entities = useMemo(
    () => getNodes(data?.entities).map(mapTaxDragEntity),
    [data?.entities]
  );
  const primaryClientState = useMemo(
    () =>
      getNodes(data?.households)[0]?.possiblePrimaryClients.find(
        ({ isPrimary }) => isPrimary
      )?.address?.stateCode,
    [data?.households]
  );

  return (
    <Stack
      direction="row"
      width="100%"
      alignItems="flex-start"
      gap={15}
      sx={{ px: 3, py: 4.5, bgcolor: 'background.paper' }}
    >
      <Box
        sx={{
          alignSelf: 'stretch',
          flex: '0 0 200px',
          pt: 5,
        }}
      >
        <Box position="sticky" top={24}>
          <InPageNavigationBox
            tabVariant="verticalTabs"
            ariaLabel="Tax drag sections"
            rows={[
              { id: TAX_DRAG_SECTION_ID, label: 'Tax drag' },
              { id: TAX_PAYER_SECTION_ID, label: 'Tax paying entity' },
            ]}
            observerOptions={{
              rootMargin: `0px 0px ${FOOTER_HEIGHT}px 0px`,
              threshold: 0.5,
            }}
            scrollOptions={{ block: 'start' }}
            contentContainerId={TAX_DRAG_EDITOR_CONTENT_ID}
            sx={{
              '& [role="tab"][aria-selected="true"], & [role="tab"]:hover': {
                bgcolor: COLORS.GRAY[50],
              },
            }}
          />
        </Box>
      </Box>

      <Stack
        id={TAX_DRAG_EDITOR_CONTENT_ID}
        spacing={4}
        sx={{ width: '100%', maxWidth: 800, minWidth: 0 }}
      >
        <Typography variant="title-l">Tax drag customization</Typography>
        <Stack spacing={3}>
          <Card
            id={TAX_DRAG_SECTION_ID}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2 }}
            data-testid="tax-drag-editor-card"
          >
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="title-m">Tax drag</Typography>
                <Typography
                  variant="body-xs"
                  sx={{ color: COLORS.TEXT.PRIMARY }}
                >
                  The share of annual investment return assumed to be paid as
                  taxes on investment income. Tax drag is applied to grantor
                  trusts, non-grantor trusts, and directly-held entities. By
                  default, this profile inherits the asset category tax drag
                  rates set at the firm level.
                </Typography>
              </Stack>

              <FormAwareRadioGroup<GrowthProfileModalFormShape>
                control={control}
                variant="card"
                size="small"
                fieldName={
                  'taxDrag.baseMode' as const satisfies GrowthProfileModalFormPaths
                }
                row
                options={[
                  {
                    label: 'Default',
                    value: TaxDragBaseMode.Default,
                    helpText:
                      'Each asset is taxed at the drag rate set for its category at the firm level.',
                  },
                  {
                    label: 'Custom entity rate',
                    value: TaxDragBaseMode.Global,
                    helpText:
                      'Apply one tax drag rate across all entities, replacing the asset category rates.',
                  },
                ]}
              />
              {taxDrag.baseMode === TaxDragBaseMode.Global && (
                <TaxDragRateField
                  label="Global tax drag"
                  rate={taxDrag.globalRate}
                  ratePath="taxDrag.globalRate"
                  stateCode={primaryClientState}
                  contextualHelp={
                    <PopperContent body={GLOBAL_TAX_DRAG_HELP_BODY} />
                  }
                />
              )}

              <EntityTaxDragOverridesEditor
                entities={entities}
                stateCode={primaryClientState}
              />
            </Stack>
          </Card>

          <Card
            id={TAX_PAYER_SECTION_ID}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2 }}
            data-testid="tax-paying-entity-card"
          >
            <TaxPayerSettings entities={entities} />
          </Card>
        </Stack>
      </Stack>
    </Stack>
  );
}
